import React, { FC } from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import ArrowDown from '@pixel/assets/icons/SortDown'
import TextField from '@pixel/core/TextField'
import { styled } from '@pixel/core/styles'
import LocalizedTypography from '../../components/LocalizedTypography'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import {useLocalizedText, useMultipleLocalizedText} from '../../contexts/Localization/localizationHooks'
import Typography from "@pixel/core/Typography";
import {AutomationSearchResult, DefaultAutomationType} from "../types/Automations";


const secondaryAutomationEnabled = window.secondaryAutomationEngineEnabled;

const ViewSectionContainer = styled('div')(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: '8px',
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}))

const AutomationViewContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
})

const ViewHeaderSection = styled('div')(({ theme }) => ({
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingBottom: theme.spacing(3),
}))

const ViewBodySection = styled('div')({
    backgroundColor: '#e4edf3',
    flexGrow: 1,
})

const ArrowDisplay = styled(ArrowDown)(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
}))

interface AutomationViewProps {
    selectedAutomation: AutomationSearchResult
}

const AutomationView: FC<AutomationViewProps> = ({
     selectedAutomation
 }) => {
    const {
        AUTOMATION_FORM_NAME,
        AUTOMATION_FORM_TYPE,
        AUTOMATION_FORM_ACCOUNT,
        AUTOMATION_SECTION_NONE_SELECTED
    } = useMultipleLocalizedText()

    const { control } = useFormContext()

    const automationType = useWatch({ name: 'automationType', control })

    return (
        <AutomationViewContainer>
            <ViewHeaderSection>
                <Controller
                    control={control}
                    name="name"
                    rules={{ maxLength: 100, required: true, }}
                    render={() => (
                        <TextField
                            id='automation-view-name-field'
                            defaultValue={selectedAutomation.name}
                            disabled
                            label={AUTOMATION_FORM_NAME}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="automationType"
                    rules={{ required: true }}
                    render={() => (
                        <TextField
                            id='automation-view-type-field'
                            defaultValue={selectedAutomation.automationType}
                            disabled
                            label={AUTOMATION_FORM_TYPE}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                        >
                        </TextField>
                    )}
                />
                {automationType === DefaultAutomationType &&
                    <Controller
                        control={control}
                        name="actionUser"
                        rules={{ maxLength: 100, required: true, }}
                        render={({ field, }) => {
                            if (typeof field.value === 'undefined') {
                                return null
                            }
                            return (
                                <TextField
                                    id='automation-view-user-field'
                                    defaultValue={selectedAutomation.actionUser?.fullName}
                                    disabled
                                    label={AUTOMATION_FORM_ACCOUNT}
                                    fullWidth
                                    sx={{ marginBottom: 1 }}
                                />
                            )
                        }}
                    />
                }
            </ViewHeaderSection>
            <ViewBodySection>
                {automationType === DefaultAutomationType &&
                    <>
                        <ViewSectionContainer>
                            <LocalizedTypography
                                variant="h3"
                                text={LocalizedText.AUTOMATION_SECTION_WHENTHISHAPPENS}
                            />
                            <Typography id='automation-view-trigger-field' variant="body1">
                                {selectedAutomation.automationObject}
                            </Typography>
                        </ViewSectionContainer>

                        <ArrowDisplay />
                    </>
                }
                <ViewSectionContainer>
                    <LocalizedTypography
                        variant="h3"
                        text={LocalizedText.AUTOMATION_SECTION_CONTINUEIF}
                    />
                    <Typography variant="body1">
                        {
                            selectedAutomation.displayFilters.split(",").map((item, index, array) => {
                                const replacedItem = item.replace(/{([^}]*)}/g, function(_, match) {
                                    return useLocalizedText(match as any);
                                });
                                return (
                                    <span key={index} id={`automation-view-condition-field-${index}`} style={{ display: 'block' }}>{replacedItem}</span>
                                );
                            })
                        }
                    </Typography>
                </ViewSectionContainer>

                <ArrowDisplay />

                <ViewSectionContainer sx={{ marginBottom: secondaryAutomationEnabled ? 0  : 3}}>
                    <LocalizedTypography
                        variant="h3"
                        text={LocalizedText.AUTOMATION_SECTION_THENDOTHIS}
                    />
                    <Typography variant="body1">
                        {
                            selectedAutomation.displayActions.split(",").map((item, index, array) => {
                                const replacedItem = item.replace(/{([^}]*)}/g, function(_, match) {
                                    return useLocalizedText(match as any);
                                });
                                return (
                                    <span key={index} id={`automation-view-action-field-${index}`} style={{ display: 'block' }}>{replacedItem}</span>
                                );
                            })
                        }
                    </Typography>
                </ViewSectionContainer>

                { secondaryAutomationEnabled ?
                    <>
                        <ArrowDisplay />

                        <ViewSectionContainer sx={{ marginBottom: 3 }}>
                            <LocalizedTypography
                                variant="h3"
                                text={LocalizedText.AUTOMATION_SECTION_RUN_SECONDARY}
                            />
                            <Typography id='automation-view-secondary-automation-field' variant="body1">
                                {selectedAutomation?.linkedAutomations[0]?.secondaryCompanyAutomation?.name || AUTOMATION_SECTION_NONE_SELECTED}
                            </Typography>
                        </ViewSectionContainer>
                    </> : null
                }
            </ViewBodySection>
        </AutomationViewContainer>
    )
}

export default AutomationView
